import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { useRole } from '../../context/RoleContext';
import { useCompanyInfo } from '../../context/CompanyInfoContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useRoutePath } from '../../utils/getRoutePath';

// Import tab components
import PersonalTab from './tabs/PersonalTab';
import ContactTab from './tabs/ContactTab';
import EmploymentTab from './tabs/EmploymentTab';
import AccessTab from './tabs/AccessTab';
import PayrollTab from './tabs/PayrollTab';
import ScheduleTab from './tabs/ScheduleTab';
import DocumentsTab from './tabs/DocumentsTab';
import OtherTab from './tabs/OtherTab';
import { useLoading } from '../../context/LoadingContext';
import { SaveIcon , ArrowBigLeft,ArrowBigRight } from 'lucide-react';

const schema = yup.object().shape({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  gender: yup.string().required('Gender is required'),
  username: yup.string().required('Username is required'),
  employee_type_id: yup.string().required('Employee type is required'),
  company_ids:yup.array().required('Company name is required'),
  
});

const CompanyWorkForceForm = () => {
  const getRoutePath = useRoutePath();  
  const { addWorkForce, updateWorkForce, getWorkForceById,getEmploymentSettings,getManagerSupervisor,getPayrollSettings,getDocumentSettings } = useCompanyWorkForce();
  const { companyInfo,getCompanyInfo } = useCompanyInfo();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [documents, setDocuments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [expLevels, setExpLevels] = useState([]);
  const [managerSupervisor, setManagerSupervisor] = useState([]);
  const [overtimeBasedOn, setOvertimeBasedOn] = useState([]);
  const [commissionRateType, setCommissionRateType] = useState([]);
  const [employeeType, setEmployeeType] = useState([]);
  const { getRoles } = useRole();
  const [roles, setRoles] = useState([]);
  const hasFetched = useRef(false);
  const hasFetchedRoles = useRef(false);
  const { showLoading,hideLoading } = useLoading();
  const [genders, setGenders] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [getEditData, setGetEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchRoles = useCallback(async () => {
    if (!hasFetchedRoles.current) {
      hasFetchedRoles.current = true;
      const response = await getRoles(0, { all: true ,is_admin:'false'});
    
      if(response){
        setRoles(response);
      }
    }
    
  }, [getRoles]);

  const fetchCompanies = useCallback(async () => {
    
    if (!hasFetched.current) {
      hasFetched.current = true;
      const response = await getCompanyInfo(null, { all: true });
      if (response?.data) {
        setCompanies(response);
      }
    }
  }, [getCompanyInfo]);

  const fetchEmploymentSettings = useCallback(async () => {
    
      const response = await getEmploymentSettings();
     
       if (response) {  

        setExpLevels(response.expLevels);
        setEmployeeType(response.employeeType);
        setGenders(response.genders);
      }
     
  }, [getEmploymentSettings]);

  const fetchManagerSupervisor = useCallback(async () => {
    const response = await getManagerSupervisor();
    if (response) {
      setManagerSupervisor(response);
    }
  }, [getManagerSupervisor]);

  const fetchPayrollSettings = useCallback(async () => {
    const response = await getPayrollSettings();
    if (response) {
      setOvertimeBasedOn(response.overtimeBasedOn);
      setCommissionRateType(response.commissionRateType);
    }
  }, [getPayrollSettings]);

  const fetchDocumentSettings = useCallback(async () => {
    const response = await getDocumentSettings();
    if (response) {
      setDocumentTypes(response.documentTypes);
    }
  }, [getDocumentSettings]);
 
  useEffect(() => {
      fetchCompanies()
  }, [fetchCompanies])

  useEffect(() => {
    fetchEmploymentSettings()
   }, [fetchEmploymentSettings])

  useEffect(() => {
    fetchManagerSupervisor()
  }, [fetchManagerSupervisor])

  useEffect(() => {
    fetchPayrollSettings()
  }, [fetchPayrollSettings])
 
  useEffect(() => {
    fetchRoles();  
    
  }, [fetchRoles])

  useEffect(() => {
    fetchDocumentSettings()
  }, [fetchDocumentSettings])

  const getDefaultValues = (existingData = {}, user = {}) => ({
    first_name: existingData.first_name || '',
    last_name: existingData.last_name || '',
    email: existingData.email || '',
    gender: existingData.gender || '',
    username: existingData.username || '',
    company_ids: existingData?.company_ids || [],
    employee_type_id: existingData.employee_type_id || '',
    image: existingData?.image?.path || '',
    is_field_worker: existingData.is_field_worker || false,
    color_code: existingData.color_code || '#356a9f',
    display_name_on_work_orders: existingData.display_name_on_work_orders || '',
    display_name_on_dispatch: existingData.display_name_on_dispatch || '',
    can_view_schedules_of: existingData.can_view_schedules_of || [],
    can_view_locations_of: existingData.can_view_locations_of || [],
    dob: existingData.dob || '',
    ssn: existingData.ssn || '',
    driver_license_number: existingData.driver_license_number || '',
    driver_license_expiry_date: existingData.driver_license_expiry_date || '',
    order_id1: existingData.order_id1 || '',
    order_id1_expiry_date: existingData.order_id1_expiry_date || '',
    order_id2: existingData.order_id2 || '',
    order_id2_expiry_date: existingData.order_id2_expiry_date || '',
    order_id3: existingData.order_id3 || '',
    order_id3_expiry_date: existingData.order_id3_expiry_date || '',
    short_bio: existingData.short_bio || '',
    primary_number: existingData.primary_number || '',
    is_mobile_number: existingData.is_mobile_number || false,
    show_primary_number_to_customer: existingData.show_primary_number_to_customer || false,
    address1: existingData.address1 || '',
    address2: existingData.address2 || '',
    city: existingData.city || '',
    state: existingData.state || '',
    zipcode: existingData.zipcode || '',
    emergency_contact_name: existingData.emergency_contact_name || '',
    emergency_contact_number: existingData.emergency_contact_number || '',
    emergency_contact_relation: existingData.emergency_contact_relation || '',
    job_title: existingData.job_title || '',
    department: existingData.department || '',
    hire_date: existingData.hire_date || '',
    original_hire_date: existingData.original_hire_date || '',
    adjusted_service_date: existingData.adjusted_service_date || '',
    release_date: existingData.release_date || '',
    manager_id: existingData.manager_id || '',
    group_assignment: existingData.group_assignment || '',
    experience_level_id: existingData.experience_level_id || '',
    is_one_time_user: existingData.is_one_time_user || false,
    active: existingData.active ?? true,
    account_locked_status: existingData.account_locked_status || false,
    tech_support_pin: existingData.tech_support_pin || '',
    employee_id: existingData.employee_id || '',
    exclude_from_hours_worked_report: existingData.exclude_from_hours_worked_report || false,
    regular_rate: existingData.regular_rate || '',
    pay_sales_commissions: existingData.pay_sales_commissions || false,
    commission_rate_type: existingData.commission_rate_type || '',
    commission_rate: existingData.commission_rate || '',
    overtime_based_on_id: existingData.overtime_based_on_id || '',
    overtime1_starts_after: existingData.overtime1_starts_after || '',
    overtime1_rate: existingData.overtime1_rate || '',
    overtime2_starts_after: existingData.overtime2_starts_after || '',
    overtime2_rate: existingData.overtime2_rate || '',
    pay_product_service_bonuses: existingData.pay_product_service_bonuses || false,
    notes: existingData.notes || '',
    role_id: existingData.role_id || '',
    
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    clearErrors,
    setError
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: getDefaultValues(getWorkForceById(id),user)
  });

  
  useEffect(() => {
    
    Object.entries(errors).forEach(([field, error]) => {
      toast.error(error.message)
     
    });
  }, [errors]);
  
   
  // Only load existing data if we're in edit mode
  useEffect(() => {
   
    if (id) {
      const existingData = getWorkForceById(id);
    
      if (existingData && typeof existingData !== 'undefined') { 
        setGetEditData(existingData);
        Object.keys(existingData).forEach(key => {
          if(key !== 'image'){
            setValue(key,existingData[key])
          }
        })
        setValue('image',existingData.image?.path)
        
        // Load existing documents
        if (existingData.documents && Array.isArray(existingData.documents)) {
          setDocuments(existingData.documents);
          setValue('documents', existingData.documents);
        }
      }
    }else{
      reset(getDefaultValues({}, user));
      setDocuments([]);
      setValue('documents', []);
      
    }
    
    if(user.role_id !== 1){
      
      //setValue('company_ids',user?.company_ids || [])  
       
    } 
  }, [id, getWorkForceById, reset, setValue,user,companyInfo,hideLoading,getEditData]);

  // Watch for profile picture changes
   

  const tabs = [
    { id: 'personal', label: 'Personal' },
    { id: 'contact', label: 'Contact' },
    { id: 'employment', label: 'Employment' },
    { id: 'access', label: 'Access' },
    { id: 'payroll', label: 'Payroll' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'documents', label: 'Documents' },
    { id: 'other', label: 'Other' }
  ];

  const handleTabChange = (tabId) => {
    
    // Get required fields for current tab
    const currentTabFields = getTabFields(activeTab);
   
    // Check all required fields
    const emptyFields = currentTabFields.filter(field => {
      const value = watch(field);
    
      // Handle falsy values like "", null, undefined, empty arrays
      if (Array.isArray(value)) {
        return value.length === 0;
      }
    
      return value === undefined || value === null || value === '';
    });
    
    if (emptyFields.length > 0) {
      
      // Set errors for all empty fields
      emptyFields.forEach(field => {
        setError(field, {
          type: 'required',
          message: `${getFieldDisplayName(field)} is required`
        });
      });
       
      // Show error toast with all missing fields
      const fieldNames = emptyFields.map(field => getFieldDisplayName(field)).join(', ');
      toast.error(`Please fill in the following required fields: ${fieldNames}`);
      return;
    } else{
      clearErrors();
    }

    // If all required fields are filled, allow tab change
    setActiveTab(tabId);
  };

  const getFieldDisplayName = (field) => {
    switch(field) {
      case 'first_name': return 'First Name';
      case 'last_name': return 'Last Name';
      case 'gender': return 'Gender';
      case 'email': return 'Email';
      case 'primary_number': return 'Primary Phone';
      case 'employee_type_id': return 'Employee Type';
      case 'job_title': return 'Job Title';
      case 'department': return 'Department';
      case 'username': return 'Username';
      default: return field;
    }
  };

  const getTabFields = (tabId) => {
    switch (tabId) {
      case 'personal':
        return ['first_name', 'last_name', 'gender'];
      case 'contact':
        return ['email', 'primary_number'];
      case 'employment':
        return ['employee_type_id','company_ids'];
      case 'access':
        return ['username','role_id'];
      default:
        return [];
    }
  };
 
  const onSubmit = async (data) => {
      showLoading();
      setIsLoading(true);
      if (id) {
        const response = await updateWorkForce(id, data);
       
        if(response.status === 200){
          toast.success('Workforce member updated successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/company-workforces'));
        }else{
          
          if (response.errors) {
            
            Object.entries(response.errors).forEach(([field, message]) => {
             // Extract actual message
            let errorMessage = '';

            if (Array.isArray(message)) {
              errorMessage = message[0]; // handle array case
            } else if (typeof message === 'object' && message !== null) {
              errorMessage = Object.values(message)[0]; // take the first value from the object
            } else {
              errorMessage = String(message); // fallback
            }

            setError(field, {
              type: 'manual',
              message: errorMessage,
            });

           
            toast.error(`${field}: ${errorMessage}`);
              
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          
          }
         
        }
      } else {

        const response = await addWorkForce(data);
       
        if(response.status === 200){
          toast.success('Workforce member added successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/company-workforces'));
        }else{
          setIsLoading(false);
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
               // Extract actual message
            let errorMessage = '';

            if (Array.isArray(message)) {
              errorMessage = message[0]; // handle array case
            } else if (typeof message === 'object' && message !== null) {
              errorMessage = Object.values(message)[0]; // take the first value from the object
            } else {
              errorMessage = String(message); // fallback
            }

            setError(field, {
              type: 'manual',
              message: errorMessage,
            });

           
            toast.error(`${field}: ${errorMessage}`);
            });
          }
        }
      }
      
  };

  const renderTabContent = () => {
     
    const commonProps = {
      register,
      errors,
      watch,
      setValue,
      clearErrors,
      setError,
      inputClassName: "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
    };
    
    switch (activeTab) {
      case 'personal':
        return <PersonalTab {...commonProps} genders={genders} />;
      case 'contact':
        return <ContactTab {...commonProps} />;
      case 'employment':
        return <EmploymentTab {...commonProps}  companies={companies} expLevels={expLevels} managerSupervisor={managerSupervisor} employeeType={employeeType} />;
      case 'access':
        return <AccessTab {...commonProps} roles={roles} getEditData={getEditData} />;
      case 'payroll':
        return <PayrollTab {...commonProps} overtimeBasedOn={overtimeBasedOn} commissionRateType={commissionRateType} />;
      case 'schedule':
        return <ScheduleTab {...commonProps} getEditData={getEditData} />;
      case 'documents':
        return <DocumentsTab {...commonProps} documents={documents} setDocuments={setDocuments} documentTypes={documentTypes} errors={errors} />;
      case 'other':
        return <OtherTab {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    
      <div className="py-6">
        <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
          <Breadcrumb />
          <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              {id ? 'Edit Workforce Member' : 'Add New Workforce Member'}
            </h1>

            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tab.id
                        ? 'border-[#009efb] text-[#009efb]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mt-6">
                {renderTabContent()}
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-center sm:justify-between md:justify-end mt-6">
                
                <div className="flex space-x-4">
                  {activeTab !== 'personal' && (
                    <button
                      type="button"
                      onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) - 1].id)}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                       <ArrowBigLeft  className="w-4 h-4 mr-2" />
                      Previous
                    </button>
                  )}
                  {activeTab !== 'other' ? (
                    <button
                      type="button"
                      onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) + 1].id)}
                      className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                    >
                      
                      Next
                      <ArrowBigRight  className="w-4 h-4 ml-2" />
                    </button>
                  ) : (
                    isLoading ? (
                      <div className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      </div>
                    ) : (
                      <button
                        type="submit"
                        className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                    >
                      <SaveIcon className="w-4 h-4 mr-2" />
                      Save
                    </button>
                    )
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    
  );
};

export default CompanyWorkForceForm; 